import { GenericReportDownload } from '@/types/downloadReports';

import { ActionType, getType } from 'typesafe-actions';

import {
  BCVA,
  CDS,
  ClosestValueTradeDate,
  TermSheetOutputTradeResponse,
  TransactionCashFlows,
  TransactionInfoAndTitle,
  TransactionNotionalSchedule,
  TransactionValueDetail,
} from '../types';

import * as transactionDetailActions from './actions';

type TransactionDetailActionType = ActionType<typeof transactionDetailActions>;

export type TransactionDetailState = {
  valueDetail: {
    data: TransactionValueDetail;
    loading: boolean;
  };
  infoAndTitle: {
    data: TransactionInfoAndTitle;
    loading: boolean;
  };
  notionalSchedule: {
    data: TransactionNotionalSchedule;
    loading: boolean;
  };
  historicalTradeReport: {
    data: GenericReportDownload;
    loading: boolean;
  };
  cashFlows: {
    data: TransactionCashFlows;
    loading: boolean;
  };
  bcva: {
    data: BCVA;
    loading: boolean;
  };

  approveTradesLoading: boolean;
  rejectTradesLoading: boolean;
  amendTradesLoading: boolean;

  cvaReport: {
    data: GenericReportDownload;
    loading: boolean;
  };
  xvaReport: {
    data: GenericReportDownload;
    loading: boolean;
  };

  cds: {
    data: CDS[];
    loading: boolean;
  };
  closestValueDate: {
    data: ClosestValueTradeDate;
    loading: boolean;
  };

  termSheetOutputTrades: {
    data: TermSheetOutputTradeResponse[];
    loading: boolean;
  };

  currency: string;

};

const initialState: TransactionDetailState = {
  valueDetail: {
    data: {
      currencies: [],
      values: [],
    },
    loading: false,
  },
  infoAndTitle: {
    data: {
      title: '',
      comp_entity_id: '',
      comp_entity_name: '',
      bk_entity_name: '',
      currency: '',
      confirmation_ref_id: null,
      bk_entity_id: '',
      is_in_review: null,
    },
    loading: false,
  },
  notionalSchedule: {
    data: {
      instrument: '',
      trade_date: '',
      counterparty: '',
      legal_identifier: '',
      bank: '',
      bank_legal_identifier: '',
      settlement_date: '',
      fx_rate: 0,
      currency_pair: '',
      leg_terms: [],
      notional_schedule: {
        pay_leg: [],
        receive_leg: [],
      },
    },
    loading: false,
  },
  historicalTradeReport: {
    data: {
      file_name: null,
      file_path: null,
      file_s3_url: null,
    },
    loading: false,
  },
  cashFlows: {
    data: {
      cash_flows: [],
      metrics: [],
      ifrs_check: false,
    },
    loading: false,
  },
  bcva: {
    data: {
      cva: {},
      dva: {},
      bilateral: {},
      bcva_data: [],
    },
    loading: false,
  },
  approveTradesLoading: false,

  cvaReport: {
    data: {
      file_name: null,
      file_path: null,
      file_s3_url: null,
    },
    loading: false,
  },
  xvaReport: {
    data: {
      file_name: null,
      file_path: null,
      file_s3_url: null,
    },
    loading: false,
  },

  cds: {
    data: [
      {
        name: '',
        cdsEntityInfo: {
          cdsEntSpread: 0,
          cdsCurveName: '',
          entName: '',
        },
        cdsCurve: {},
      },
    ],
    loading: false,
  },

  closestValueDate: {
    data: {
      value_date: '',
    },
    loading: false,
  },

  termSheetOutputTrades: {
    data: [],
    loading: false,
  },

  rejectTradesLoading: false,
  amendTradesLoading: false,

  currency: '',
};

const transactionDetailReducer = (
  state: TransactionDetailState = initialState,
  action: TransactionDetailActionType,
): TransactionDetailState => {
  switch (action.type) {
    case getType(transactionDetailActions.getTransactionDetails.request):
      return {
        ...state,
        valueDetail: {
          ...state.valueDetail,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getTransactionDetails.success):
      return {
        ...state,
        valueDetail: {
          ...state.valueDetail,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getTransactionDetails.failure):
      return {
        ...state,
        valueDetail: {
          ...state.valueDetail,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getTransactionInfoAndTitle.request):
      return {
        ...state,
        infoAndTitle: {
          ...state.infoAndTitle,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getTransactionInfoAndTitle.success):
      return {
        ...state,
        infoAndTitle: {
          ...state.infoAndTitle,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getTransactionInfoAndTitle.failure):
      return {
        ...state,
        infoAndTitle: {
          ...state.infoAndTitle,
          loading: false,
        },
      };
    case getType(
      transactionDetailActions.getTransactionNotionalSchedule.request,
    ):
      return {
        ...state,
        notionalSchedule: {
          ...state.notionalSchedule,
          loading: true,
        },
      };
    case getType(
      transactionDetailActions.getTransactionNotionalSchedule.success,
    ):
      return {
        ...state,
        notionalSchedule: {
          ...state.notionalSchedule,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(
      transactionDetailActions.getTransactionNotionalSchedule.failure,
    ):
      return {
        ...state,
        notionalSchedule: {
          ...state.notionalSchedule,
          loading: false,
        },
      };

    // historical trade report
    case getType(
      transactionDetailActions.getHistoricalTradeReportDownload.request,
    ):
      return {
        ...state,
        historicalTradeReport: {
          ...state.historicalTradeReport,
          loading: true,
        },
      };
    case getType(
      transactionDetailActions.getHistoricalTradeReportDownload.success,
    ):
      return {
        ...state,
        historicalTradeReport: {
          ...state.historicalTradeReport,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(
      transactionDetailActions.getHistoricalTradeReportDownload.failure,
    ):
      return {
        ...state,
        historicalTradeReport: {
          ...state.historicalTradeReport,
          loading: false,
        },
      };

    //cashflows
    case getType(transactionDetailActions.getTransactionCashflows.request):
      return {
        ...state,
        cashFlows: {
          ...state.cashFlows,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getTransactionCashflows.success):
      return {
        ...state,
        cashFlows: {
          ...state.cashFlows,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getTransactionCashflows.failure):
      return {
        ...state,
        cashFlows: {
          ...state.cashFlows,
          loading: false,
        },
      };

    //bcva data
    case getType(transactionDetailActions.getBCVA.request):
      return {
        ...state,
        bcva: {
          ...state.bcva,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getBCVA.success):
      return {
        ...state,
        bcva: {
          ...state.bcva,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getBCVA.failure):
      return {
        ...state,
        bcva: {
          ...state.bcva,
          loading: false,
        },
      };

    //aprove trades
    case getType(transactionDetailActions.approveTrade.request):
      return {
        ...state,
        approveTradesLoading: true,
      };
    case getType(transactionDetailActions.approveTrade.success):
      return {
        ...state,
        approveTradesLoading: false,
      };
    case getType(transactionDetailActions.approveTrade.failure):
      return {
        ...state,
        approveTradesLoading: false,
      };

    //cva report download
    case getType(transactionDetailActions.getCVAReportDownload.request):
      return {
        ...state,
        cvaReport: {
          ...state.cvaReport,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getCVAReportDownload.success):
      return {
        ...state,
        cvaReport: {
          ...state.cvaReport,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getCVAReportDownload.failure):
      return {
        ...state,
        cvaReport: {
          ...state.cvaReport,
          loading: false,
        },
      };
    //xva report download
    case getType(transactionDetailActions.getXVAReportDownload.request):
      return {
        ...state,
        xvaReport: {
          ...state.xvaReport,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getXVAReportDownload.success):
      return {
        ...state,
        xvaReport: {
          ...state.xvaReport,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getXVAReportDownload.failure):
      return {
        ...state,
        xvaReport: {
          ...state.xvaReport,
          loading: false,
        },
      };
    //cds data
    case getType(transactionDetailActions.getCDS.request):
      return {
        ...state,
        cds: {
          ...state.cds,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getCDS.success):
      return {
        ...state,
        cds: {
          ...state.cds,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getCDS.failure):
      return {
        ...state,
        cds: {
          ...state.cds,
          loading: false,
        },
      };

    // closest value date
    case getType(transactionDetailActions.getClosestValueDate.request):
      return {
        ...state,
        closestValueDate: {
          ...state.closestValueDate,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getClosestValueDate.success):
      return {
        ...state,
        closestValueDate: {
          ...state.closestValueDate,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getClosestValueDate.failure):
      return {
        ...state,
        closestValueDate: {
          ...state.closestValueDate,
          loading: false,
        },
      };

    // term sheet output trades
    case getType(transactionDetailActions.getTermsheetOutputTrades.request):
      return {
        ...state,
        termSheetOutputTrades: {
          ...state.termSheetOutputTrades,
          loading: true,
        },
      };
    case getType(transactionDetailActions.getTermsheetOutputTrades.success):
      return {
        ...state,
        termSheetOutputTrades: {
          ...state.termSheetOutputTrades,
          data: action.payload.data,
          loading: false,
        },
      };
    case getType(transactionDetailActions.getTermsheetOutputTrades.failure):
      return {
        ...state,
        termSheetOutputTrades: {
          ...state.termSheetOutputTrades,
          loading: false,
        },
      };

    case getType(transactionDetailActions.reset):
      return {
        ...state,
        cds: initialState.cds,
        bcva: initialState.bcva,
        cashFlows: initialState.cashFlows,
      };

    //reject trade
    case getType(transactionDetailActions.rejectTrade.request):
      return {
        ...state,
        rejectTradesLoading: true,
      };
    case getType(transactionDetailActions.rejectTrade.success):
      return {
        ...state,
        rejectTradesLoading: false,
      };
    case getType(transactionDetailActions.rejectTrade.failure):
      return {
        ...state,
        rejectTradesLoading: false,
      };

    //amend trade
    case getType(transactionDetailActions.amendTrade.request):
      return {
        ...state,
        amendTradesLoading: true,
      };
    case getType(transactionDetailActions.amendTrade.success):
      return {
        ...state,
        amendTradesLoading: false,
      };
    case getType(transactionDetailActions.amendTrade.failure):
      return {
        ...state,
        amendTradesLoading: false,
      };

    // store currency selected from trade values widget

    case getType(transactionDetailActions.selectCurrency):
      return {
        ...state,
        currency: action.payload,
      };

    default:
      return state;
  }
};

export default transactionDetailReducer;
